import * as Sentry from "@sentry/browser";
import { z } from "zod/mini";

import { getProductType } from "../getProductType";

import type { OrderData } from "../../types/OrderData";
import type { ProductItem } from "../../types/ProductItem";
import type { ReceiptData } from "containers/account/receipt/AccountReceipt";

const receiptOrderDataSchema = z.object({
  orderId: z.string().check(z.minLength(1)),
  purchasedAt: z.number(),
  totalPrice: z.number(),
  shippingFee: z.number(),
  serviceFee: z.number(),
  settlementFee: z.number(),
});

/** 領収書に載せる注文の値。検証済みであることを型で示す */
type ReceiptOrderData = z.infer<typeof receiptOrderDataSchema>;

/**
 * 注文データが領収書に載せられる形かを検証する。
 * 不正な値から組み立てると金額0・明細なしの領収書が静かに出てしまうため、
 * 期待した形でないときはnullを返し、発生に気づけるようSentryへ送る。
 * 明細とは独立に注文データだけを見るので、注文ごとに1回だけ呼べば足りる
 */
export const parseReceiptOrderData = (
  orderData: OrderData
): ReceiptOrderData | null => {
  const { data, success, error } = receiptOrderDataSchema.safeParse(orderData);
  if (!success) {
    console.error("Invalid order data:", error);
    Sentry.captureException(
      new Error(`Invalid order data for receipt: ${error.message}`)
    );
    return null;
  }
  return data;
};

type CreateReceiptDataInput = {
  orderData: ReceiptOrderData;
  orderList: ProductItem[];
  userId: string;
  userName: string;
};

/** 検証済みの注文データと明細から領収書の表示データを組み立てる */
export const createReceiptData = (
  input: CreateReceiptDataInput
): ReceiptData => {
  const { orderData, orderList, userId, userName } = input;
  const {
    orderId,
    totalPrice,
    purchasedAt,
    shippingFee,
    serviceFee,
    settlementFee,
  } = orderData;

  const effectiveDate = new Date(purchasedAt * 1000);
  const breakdown = orderList.map((item) => {
    const productType = getProductType({ productType: item.type });

    return {
      eventTitle: item.eventTitle,
      productType,
      name: item.variationName,
      price: item.price,
      quantity: item.quantity,
    };
  });

  return {
    effectiveDate,
    spwnId: userId,
    userName,
    orderId,
    shippingFee,
    serviceFee,
    settlementFee,
    total: totalPrice,
    breakdown,
  };
};
